import { riskLevelToLabel } from '../../../../utils/constants';
import { updateMoment } from '../../RsdForms/api';

/**
 * Field Update Utility for Non-Phased Layout
 *
 * This utility handles the proper nesting of field updates based on the API schema.
 * Different fields need to be nested under different parent objects.
 */

// Define which fields belong to which parent object
export const FIELD_MAPPING = {
  // Fields that go under generalInfo
  generalInfo: [
    'momentType',
    'riskLevel',
    'tier',
    'event',
    'parentMoment',
    'revenueImpact' // ✅ FIX: Revenue impact must be nested under generalInfo
  ] as string[],

  // Fields that go under dates (nested under generalInfo)
  dates: [
    'targetStart',
    'targetEnd',
    'releaseDate',
    'targetMonth',
    'targetQuarter',
    'releaseMonth',
    'releaseQuarter',
    'effectiveDate',
    'effectiveMonth',
    'effectiveQuarter',
    'targetDateStatus',
    'releaseDateStatus',
    'effectiveDateStatus'
  ] as string[],

  // Fields that go at root level
  root: [
    'name',
    'products',
    'tags',
    'showOnRoadmap', // Used for Offer, Price Change, or Program moments (formerly mirrorRelease)
    'inheritMilestones',
    'mirrorRelease', // Legacy field - kept for backward compatibility
    'status',
    'secondaryDateStatus' // Active/Completed status for the secondary date, independent of showOnRoadmap
  ] as string[],

  // Fields that go under primaryContacts
  primaryContacts: [
    'productMarketingManagers',
    'programManagers',
    'productManagers'
  ] as string[],

  // Fields that go under overview
  overview: [
    'headline',
    'launchSummary',
    'goals',
    'targetAudience',
    'howIsItDifferent',
    'launchRegion',
    'audienceStrategicAlignment',
    'mvpStatus'
  ] as string[]
};

/**
 * Creates the proper payload structure for updating a field
 * @param fieldName - The name of the field being updated
 * @param value - The new value for the field
 * @param currentData - Current form data (optional, for complex updates)
 * @returns Properly nested payload for the API
 */
export function createFieldUpdatePayload(
  fieldName: string,
  value: any,
  currentData?: any
): Record<string, any> {
  const payloadValue =
    fieldName === 'riskLevel' ? riskLevelToLabel(value) : value;

  // Check which categories the field belongs to
  for (const [categories, fields] of Object.entries(FIELD_MAPPING)) {
    if (fields.includes(fieldName)) {
      switch (categories) {
        case 'generalInfo':
          return {
            generalInfo: {
              [fieldName]: payloadValue
            }
          };

        case 'dates':
          return {
            generalInfo: {
              dates: {
                [fieldName]: payloadValue
              }
            }
          };

        case 'root':
          return {
            [fieldName]: payloadValue
          };

        case 'primaryContacts':
          return {
            primaryContacts: {
              [fieldName]: payloadValue
            }
          };

        case 'overview':
          return {
            overview: {
              [fieldName]: payloadValue
            }
          };

        default:
          return { [fieldName]: payloadValue };
      }
    }
  }

  // If field is not found in mapping, use root level
  return { [fieldName]: payloadValue };
}

/**
 * Generic field update function for non-phased layout
 * @param issueId - The issue ID to update
 * @param fieldName - The name of the field being updated
 * @param value - The new value for the field
 * @param onSuccess - Optional callback for successful update
 * @param onError - Optional callback for error handling
 */
export async function updateNonPhasedField(
  issueId: string,
  fieldName: string,
  value: any,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): Promise<void> {
  if (!issueId) {
    onError?.(new Error('No issue ID provided'));
    return;
  }

  try {
    const payload = createFieldUpdatePayload(fieldName, value);
    const response = await updateMoment(issueId, payload);
    onSuccess?.(response);
  } catch (error) {
    onError?.(error);
  }
}

/**
 * Specialized update function for event field (which has a complex structure)
 * @param issueId - The issue ID to update
 * @param eventData - The event object with id and name
 * @param onSuccess - Optional callback for successful update
 * @param onError - Optional callback for error handling
 */
export async function updateEventField(
  issueId: string,
  eventData: { id: string | null; name: string },
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): Promise<void> {
  return updateNonPhasedField(issueId, 'event', eventData, onSuccess, onError);
}

/**
 * Specialized update function for parent moment field (which has a complex structure)
 * @param issueId - The issue ID to update
 * @param parentMomentData - The parent moment object with id and name
 * @param onSuccess - Optional callback for successful update
 * @param onError - Optional callback for error handling
 */
export async function updateParentMomentField(
  issueId: string,
  parentMomentData: { id: string | null; name: string },
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): Promise<void> {
  return updateNonPhasedField(
    issueId,
    'parentMoment',
    parentMomentData,
    onSuccess,
    onError
  );
}

/**
 * Specialized update function for dates (which are nested under generalInfo.dates)
 * @param issueId - The issue ID to update
 * @param dateField - The specific date field (targetStart, targetEnd, etc.)
 * @param dateValue - The new date value
 * @param onSuccess - Optional callback for successful update
 * @param onError - Optional callback for error handling
 */
export async function updateDateField(
  issueId: string,
  dateField: string,
  dateValue: string,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): Promise<void> {
  return updateNonPhasedField(
    issueId,
    dateField,
    dateValue,
    onSuccess,
    onError
  );
}

/**
 * Specialized update function for status field in non-phased layout
 * This function includes statusUpdatedAt timestamp when status is COMPLETED
 * @param issueId - The issue ID to update
 * @param status - The new status (ACTIVE, COMPLETED, N/A)
 * @param onSuccess - Success callback
 * @param onError - Error callback
 * @param completionDate - The completion date (required for COMPLETED status)
 */
export async function updateNonPhasedStatusField(
  issueId: string,
  status: string,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void,
  completionDate?: string
): Promise<void> {
  if (!issueId) {
    onError?.(new Error('No issue ID provided'));
    return;
  }

  // Create a custom payload for status updates that includes completion date
  const payload = {
    status: status,
    ...(status === 'COMPLETED' &&
      completionDate && { statusUpdatedAt: completionDate })
  };

  try {
    const response = await updateMoment(issueId, payload);
    onSuccess?.(response);
  } catch (error) {
    onError?.(error);
  }
}

/**
 * Helper function to get the proper field categories
 * @param fieldName - The field name to categorize
 * @returns The categories the field belongs to
 */
export function getFieldCategory(fieldName: string): string {
  for (const [categories, fields] of Object.entries(FIELD_MAPPING)) {
    if (fields.includes(fieldName)) {
      return categories;
    }
  }
  return 'root';
}

export default {
  createFieldUpdatePayload,
  updateNonPhasedField,
  updateNonPhasedStatusField,
  updateEventField,
  updateParentMomentField,
  updateDateField,
  getFieldCategory,
  FIELD_MAPPING
};
